import { init, logEvent } from '@amplitude/analytics-browser'
import React, { JSX, Suspense, useContext, useEffect, useState } from 'react'
import { Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import { iMSCContext } from '@/common/iMSCContext'

import { isEmpty } from '@/utils/common'
import { lazyWithRetry } from '@/utils/lazyImport'

import { ReturnDTO, setAuthorizationToken } from '@/apis/http'
import AuthService from '@/apis/AuthService'

import rootStyle from '@/scss/parenting-test/style.module.scss'

// Common
const Main = lazyWithRetry(() => import('@/pages/Main'))
const Terms = lazyWithRetry(() => import('@/pages/Terms'))
const Quit = lazyWithRetry(() => import('@/pages/Quit'))

// Account
const Signin = lazyWithRetry(() => import('@/pages/acc/Signin'))
const FindAccount = lazyWithRetry(() => import('@/pages/acc/FindAccount'))
const Signup = lazyWithRetry(() => import('@/pages/acc/Signup'))
const ChangePassword = lazyWithRetry(() => import('@/pages/acc/ChangePassword'))

// Component
const CompBanner = lazyWithRetry(() => import('@/components/CompBanner'))
const CompFooter = lazyWithRetry(() => import('@/components/CompFooter'))
const CompHeader = lazyWithRetry(() => import('@/components/CompHeader'))
const CompPath = lazyWithRetry(() => import('@/components/CompPath'))
const CompSideMenu = lazyWithRetry(() => import('@/components/CompSideMenu'))

// Introduce
const WhyMSC = lazyWithRetry(() => import('@/pages/introduce/WhyMSC'))
const Greetings = lazyWithRetry(() => import('@/pages/introduce/Greetings'))
const Branch = lazyWithRetry(() => import('@/pages/introduce/Branches'))
const Map = lazyWithRetry(() => import('@/pages/introduce/Maps'))
const Recruit = lazyWithRetry(() => import('@/pages/introduce/Recruit'))

// Program
const Overview = lazyWithRetry(() => import('@/pages/program/Overview'))
const Elementary = lazyWithRetry(() => import('@/pages/program/Elementary'))
const High = lazyWithRetry(() => import('@/pages/program/High'))
const SmartReading = lazyWithRetry(() => import('@/pages/program/SmartReading'))
const Science = lazyWithRetry(() => import('@/pages/program/Science'))
const SpecialDesc = lazyWithRetry(() => import('@/pages/program/SpecialDesc'))

// VOD/
const Seminar = lazyWithRetry(() => import('@/pages/vod/Seminar'))
const Peek = lazyWithRetry(() => import('@/pages/vod/Peek'))

// Press
const News = lazyWithRetry(() => import('@/pages/press/News'))
const Columns = lazyWithRetry(() => import('@/pages/press/Columns'))
const Promotion = lazyWithRetry(() => import('@/pages/press/Promotion'))

// Enroll
const Guide = lazyWithRetry(() => import('@/pages/enroll/Guide'))
const Presentation = lazyWithRetry(() => import('@/pages/enroll/Presentation'))
const EntranceTest = lazyWithRetry(() => import('@/pages/enroll/EntranceTest'))
const EntranceTestPay = lazyWithRetry(() => import('@/pages/enroll/pay/EntranceTestPay'))
const Counseling = lazyWithRetry(() => import('@/pages/enroll/Counseling'))

// Zone
const Notice = lazyWithRetry(() => import('@/pages/zone/Notice'))
const Learning = lazyWithRetry(() => import('@/pages/zone/Learning'))
const BestReport = lazyWithRetry(() => import('@/pages/zone/BestReport'))
const ExperienceCase = lazyWithRetry(() => import('@/pages/zone/ExperienceCase'))
const FAQ = lazyWithRetry(() => import('@/pages/zone/FAQ'))
const Rules = lazyWithRetry(() => import('@/pages/zone/Rules'))

// User
const Account = lazyWithRetry(() => import('@/pages/user/Account'))
const Applications = lazyWithRetry(() => import('@/pages/user/Applications'))

// Board
const BoardDetail = lazyWithRetry(() => import('@/pages/board/BoardDetail'))

// Parent Test + Student Retest
const TestMain = lazyWithRetry(() => import('@/pages/enquiry/TestMain'))
const RetestMain = lazyWithRetry(() => import('@/pages/enquiry/RetestMain'))
const TestTerms = lazyWithRetry(() => import('@/pages/enquiry/TestTerms'))
const Testing = lazyWithRetry(() => import('@/pages/enquiry/Testing'))
const Retesting = lazyWithRetry(() => import('@/pages/enquiry/Retesting'))
const TestDone = lazyWithRetry(() => import('@/pages/enquiry/TestDone'))

const DaumPostcode = lazyWithRetry(() => import('@/pages/DaumPostcode'))

const CompLoading = lazyWithRetry(() => import('@/components/CompLoading'))
const CompModal = lazyWithRetry(() => import('@/components/CompModal'))

type layoutType = {
  sideNav: boolean
  children?: React.ReactNode
}

Kakao.init('4b0cad0f46944031046cc53746ef5bc0')

/**
 * @component
 * * 테스트.
 */
function Enquiry() {
  return (
    <div className={rootStyle.Root}>
      <Outlet />
    </div>
  )
}

/**
 * @component
 * * 인증 확인.
 */
function RequireAuth({ children }: { children: JSX.Element }) {
  const { isLoggedIn } = useContext(iMSCContext)

  return isLoggedIn ? children : <Signin />
}

/**
 * @component
 * * MSC 기본 레이아웃.
 */
function FullscreenLayout(props: layoutType): React.ReactElement {
  const [isShowFullscreen, setIsShowFullscreen] = useState(false)

  const location = useLocation()

  useEffect(() => {
    const path = location.pathname

    setIsShowFullscreen(path === '/')

    // 비밀번호 초기화 제외.
    if (!path.startsWith('/change/password')) {
      // 게시판 상세보기 log 간략하게.
      if (path.startsWith('/board')) {
        const itemIdIdx = path.indexOf('/', 1)
        const itemId = path.substring(itemIdIdx + 1)
        logEvent('/board', { itemId })
      } else {
        logEvent(path)
      }
    }
  }, [location])

  return (
    <>
      {!isShowFullscreen && <CompBanner />}
      {!isShowFullscreen && props.sideNav && <CompPath />}

      <div id="contents" style={isShowFullscreen ? { backgroundColor: '#F4F4F5' } : {}}>
        <div className="wrapper">
          <div className="container">
            {!isShowFullscreen && props.sideNav && <CompSideMenu />}

            <div className="contents-area">{props.children}</div>
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * @components
 * * App.
 */
function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isShowSideMenu, setIsShowSideMenu] = useState(false)
  const [mainCategory, setMainCategory] = useState('default')
  const [subCategory, setSubCategory] = useState('why_msc')
  const [sideMenuIdx, setSideMenuIdx] = useState(0)
  const [isAuth, setIsAuth] = useState(false)

  const [isShowLogoutModal, setIsShowLogoutModal] = useState(false)

  const navigate = useNavigate()

  const ctxValue = {
    isLoggedIn,
    isShowSideMenu,
    mainCategory,
    subCategory,
    sideMenuIdx,
    isAuth,
    setIsLoggedIn: (value: boolean) => setIsLoggedIn(value),
    setIsShowSideMenu: (value: boolean) => setIsShowSideMenu(value),
    setMainCategory: (value: string) => setMainCategory(value),
    setSubCategory: (value: string) => setSubCategory(value),
    setSideMenuIdx: (value: number) => setSideMenuIdx(value),
    setIsAuth: (value: boolean) => setIsAuth(value),
  }

  /**
   * 사용자 아이콘 클릭.
   */
  function onUserClick() {
    if (!isLoggedIn) navigate('/signin')
  }

  /**
   * CompHeader 로그아웃 선택.
   */
  function onLogoutClick() {
    setIsShowLogoutModal(true)
  }

  /**
   * 사용자 로그아웃 실행.
   */
  function performLogout() {
    AuthService.logout<ReturnDTO>()
      .then(({ resultCode, resultMsg }) => {
        if (resultCode === 0) {
          setIsShowLogoutModal(false)

          delete localStorage['userSeqId']
          delete localStorage['memberId']
          delete localStorage['enrollType']

          setAuthorizationToken()
          setIsLoggedIn(false)

          navigate('/', { replace: true })
        } else {
          alert(resultMsg)
        }
      })
      .catch((error) => {
        alert(error.resultMsg ?? error)
      })
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      init(import.meta.env.VITE_AMPLITUDE_KEY, { defaultTracking: true })
    }

    const userSeqId = localStorage.getItem('userSeqId')
    const memberId = localStorage.getItem('memberId')
    const enrollType = localStorage.getItem('enrollType')

    if (!userSeqId && !memberId && !enrollType) {
      return
    }

    if (userSeqId && memberId && enrollType) {
      AuthService.silentRefresh<ReturnDTO>()
        .then(({ resultCode, resultData }) => {
          if (resultCode === 0) {
            const newUserSeqId = resultData?.userSeqId
            const newMemberId = resultData?.memberId
            const newEnrollType = resultData?.enrollType

            if (
              newUserSeqId == userSeqId &&
              newMemberId == memberId &&
              newEnrollType == enrollType
            ) {
              const accessToken = resultData?.accessToken
              setAuthorizationToken(accessToken)
              setIsLoggedIn(!isEmpty(accessToken))
            } else {
              throw new Error('사용자 인증 식별 코드가 일치하지 않아 로그아웃 됩니다.')
            }
          } else {
            throw new Error('사용자 인증에 실패하여 로그아웃 됩니다.')
          }
        })
        .catch((e) => {
          if (e.message) {
            alert(e.message)
          } else if (e.resultMsg) {
            alert(e.resultMsg)
          }
          navigate('/quit', { replace: true })
        })
    } else {
      navigate('/quit', { replace: true })
    }
  }, [])

  return (
    <iMSCContext.Provider value={ctxValue}>
      <Suspense fallback={<CompLoading />}>
        {location.pathname === '/address' ? (
          <Routes>
            <Route path="/address" element={<DaumPostcode />} />
          </Routes>
        ) : location.pathname.startsWith('/test') ||
          location.pathname.startsWith('/retest') ? (
          <Routes>
            {/* Parenting test */}
            <Route path="/test" element={<Enquiry />}>
              <Route index element={<TestMain />} />
              <Route path="terms" element={<TestTerms />} />
              <Route path="testing" element={<Testing />} />
              <Route path="done" element={<TestDone />} />
            </Route>
            {/* Student Retest */}
            <Route path="/retest" element={<Enquiry />}>
              <Route index element={<RetestMain />} />
              <Route path="testing" element={<Retesting />} />
            </Route>
          </Routes>
        ) : location.pathname === '/counseling' ? (
          <Routes>
            <Route path="/counseling" element={<Counseling />} />
          </Routes>
        ) : (
          <>
            <CompHeader onAccClick={onUserClick} onLogoutClick={onLogoutClick} />

            <FullscreenLayout sideNav={isShowSideMenu}>
              <Routes>
                {/* Common */}
                <Route path="/" element={<Main />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/quit" element={<Quit />} />
                {/* Account */}
                <Route path="/signin/*" element={<Signin />} />
                <Route path="/findAccount" element={<FindAccount />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/change/password/" element={<ChangePassword />}>
                  <Route path=":urlCode" element={<ChangePassword />} />
                </Route>
                {/* Introduce */}
                <Route path="/whymsc" element={<WhyMSC />} />
                <Route path="/greetings" element={<Greetings />} />
                <Route path="/branches" element={<Branch />} />
                <Route path="/maps" element={<Map />} />
                <Route path="/recruit" element={<Recruit />} />
                {/* Program */}
                <Route path="/overview" element={<Overview />} />
                <Route path="/elementary" element={<Elementary />} />
                <Route path="/high" element={<High />} />
                <Route path="/smartReading" element={<SmartReading />} />
                <Route path="/science" element={<Science />} />
                <Route path="/specialDesc" element={<SpecialDesc />} />
                {/* VOD */}
                <Route path="/seminar" element={<Seminar />} />
                <Route path="/peek" element={<Peek />} />
                {/* Press */}
                <Route path="/news" element={<News />} />
                <Route path="/columns" element={<Columns />} />
                <Route path="/promotion" element={<Promotion />} />
                {/* Enroll */}
                <Route path="/guide" element={<Guide />} />
                <Route path="/presentation" element={<Presentation />} />
                <Route
                  path="/entranceTest"
                  element={
                    <RequireAuth>
                      <EntranceTest />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/entranceTestPay"
                  element={
                    <RequireAuth>
                      <EntranceTestPay />
                    </RequireAuth>
                  }
                />
                {/* Zone */}
                <Route path="/notice" element={<Notice />} />
                <Route path="/learning" element={<Learning />} />
                <Route path="/bestReport" element={<BestReport />} />
                <Route path="/experienceCase" element={<ExperienceCase />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/rules" element={<Rules />} />
                {/* User */}
                <Route
                  path="/account"
                  element={
                    <RequireAuth>
                      <Account />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/applications"
                  element={
                    <RequireAuth>
                      <Applications />
                    </RequireAuth>
                  }
                />
                {/* Board */}
                <Route path="/board/:id" element={<BoardDetail />} />
              </Routes>
            </FullscreenLayout>

            <CompFooter />

            {isShowLogoutModal && (
              <CompModal
                modalTitle="로그아웃"
                onCloseClick={() => setIsShowLogoutModal(false)}
                onConfirmClick={performLogout}
              >
                <span>로그아웃 하시겠습니까?</span>
              </CompModal>
            )}
          </>
        )}
      </Suspense>
    </iMSCContext.Provider>
  )
}

export default App
